import React, { ReactNode, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableCell,
  TableBody,
  CircularProgress,
  TableContainer,
  TableRow,
  TableFooter,
  TablePagination,
  Box,
  Typography
} from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../common/interfaces/common.interface';
import { ReactComponent as ArrowIcon } from '../../assets/arrow-icon.svg';
import { SortOrderEnum } from '../../common/enums/common.enum';
import { ReactComponent as ListEmptyStateIcon } from '../../assets/list-empty-state-icon.svg';

const BasicTable: React.FC<{
  headings: ITableHeadings[];
  loader: boolean;
  children: ReactNode;
  metadata: ITableMetadata;
  setMetadata: React.Dispatch<React.SetStateAction<ITableMetadata>>;
  sortBy?: string | null;
  sortOrder?: SortOrderEnum;
  onSortChange?: (sortBy: string | null, sortOrder: 'asc' | 'desc' | null) => void;
}> = ({ headings, children, loader, metadata, setMetadata, sortBy, sortOrder, onSortChange }) => {
  const handlePageChange = (page: number) => {
    setMetadata({ ...metadata, currentPage: page + 1 });
  };

  const handleRowsPerPageChange = (newItemsPerPage: number) => {
    setMetadata({
      ...metadata,
      itemsPerPage: newItemsPerPage,
      currentPage: 1
    });
  };

  const handleSortClick = (key?: string) => {
    if (!key || !onSortChange) return;

    if (key === sortBy) {
      if (sortOrder === 'desc') {
        onSortChange(key, 'asc');
      } else if (sortOrder === 'asc') {
        onSortChange(null, null);
      } else {
        onSortChange(key, 'desc');
      }
    } else {
      onSortChange(key, 'desc');
    }
  };

  const paginationInfo = useMemo(() => {
    return `${metadata.totalItems > 0 ? (metadata.currentPage - 1) * metadata.itemsPerPage + 1 : metadata.currentPage - 1} - ${Math.min(
      metadata.totalItems,
      metadata.itemsPerPage * metadata.currentPage
    )} of ${metadata.totalItems}`;
  }, [metadata]);

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }} aria-label="sortable table">
        <TableHead>
          <TableRow sx={{ padding: 1 }}>
            {headings.map(({ id, label, sortable, sortKey, width }) => {
              const isActive = sortable && sortKey === sortBy;
              return (
                <TableCell
                  key={id}
                  onClick={() => sortable && handleSortClick(sortKey)}
                  sx={{
                    cursor: sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? 'primary.main' : 'text.primary',
                    backgroundColor: '#FBFBFC',
                    width: width ?? 'auto',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflow: 'hidden'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {sortable && (
                      <ArrowIcon
                        style={{
                          transform: isActive && sortOrder === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
                          color: isActive ? '#3F3273' : '#C0C0C0',
                          opacity: isActive ? 1 : 0.5,
                          width: 16,
                          height: 16
                        }}
                      />
                    )}
                    <span>{label}</span>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loader ? (
            <TableRow
              sx={{
                minHeight: '225px'
              }}
            >
              <TableCell colSpan={headings.length} sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : metadata.totalItems === 0 ? (
            <TableRow>
              <TableCell colSpan={headings.length} sx={{ textAlign: 'center', py: 6 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                  <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    No results found for selected filters
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              onPageChange={(event, selectedPage) => {
                handlePageChange(selectedPage);
              }}
              sx={{ border: 0 }}
              page={metadata.currentPage - 1}
              count={metadata.totalItems}
              rowsPerPage={metadata.itemsPerPage}
              labelRowsPerPage="Rows per page"
              onRowsPerPageChange={event => {
                handleRowsPerPageChange(Number(event.target.value));
              }}
              labelDisplayedRows={() => paginationInfo}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};
export default BasicTable;
